import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, UploadCloud, Sparkles, Briefcase, Building2, Mail, KeyRound, RefreshCw, Loader2, Edit2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import WorldMapBackground from "@/components/WorldMapBackground";
import { useAuth } from "@/context/AuthContext";
import { MatchRing } from "@/components/ui-bits";
import { CURRENCIES } from "@/data/mockData";
import { api, formatApiError } from "@/lib/api";

const WORK_MODELS = ["Remote", "Hybrid", "Onsite"];

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, ready, register, verifyOtp, resendOtp } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const planParam = searchParams.get("plan");
  const packParam = searchParams.get("pack");

  const [stage, setStage] = useState("form"); // "form" | "otp" | "processing" | "done"
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "job_seeker",
    current_role: "", years_experience: "", target_role: "", locations: "", work_models: ["Remote"],
    salary_currency: "USD", target_salary: "140k", cv_name: "", company: "", title: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (ready && user) {
    if ((planParam && planParam !== "Free") || packParam) {
      const targetBase = user.role === "recruiter" ? "/recruiter/pricing" : "/app/pricing";
      return <Navigate to={`${targetBase}${location.search}`} replace />;
    }
    return <Navigate to={user.role === "recruiter" ? "/recruiter" : "/app"} replace />;
  }

  const seeker = form.role === "job_seeker";
  const steps = seeker ? ["Account", "Your role", "Preferences", "Your CV"] : ["Account", "Company"];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setError("");
    setLoading(true);
    const currSym = CURRENCIES.find((c) => c.code === (form.salary_currency || "USD"))?.symbol || "$";
    const salaryVal = form.target_salary
      ? (form.target_salary.startsWith("$") || form.target_salary.startsWith("€") || form.target_salary.startsWith("£") || form.target_salary.startsWith("₹")
          ? form.target_salary
          : `${currSym}${form.target_salary}`)
      : `${currSym}140k`;

    const selectedModels = form.work_models && form.work_models.length > 0 ? form.work_models : ["Remote"];
    const profile = seeker
      ? {
          current_role: form.current_role || "Product Analyst",
          years_experience: parseFloat(form.years_experience) || 0,
          target_role: form.target_role || "Product Manager",
          locations: (form.locations || "Remote").split(",").map((x) => x.trim()),
          work_model: selectedModels.join(" / "),
          work_models: selectedModels,
          target_salary: salaryVal,
          skills: ["Product Strategy", "SQL", "Python", "Analytics", "Stakeholder Management"],
          profile_strength: 86,
        }
      : { company: form.company || "Nimbus Labs", title: form.title || "Recruiter" };

    const res = await register({
      name: form.name, email: form.email, password: form.password, role: form.role, profile,
    });
    setLoading(false);

    if (!res.ok) { setError(res.error); return; }

    if (res.require_otp) {
      toast.success("Verification code sent to your email!");
      setResendCooldown(60);
      setStage("otp");
    } else {
      await proceedToCompletion();
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp || otp.trim().length < 6) {
      setOtpError("Please enter the 6-digit verification code.");
      return;
    }
    setOtpError("");
    setVerifying(true);
    const res = await verifyOtp(form.email, otp.trim());
    setVerifying(false);

    if (!res.ok) {
      setOtpError(res.error);
      return;
    }

    toast.success("Email verified successfully!");
    await proceedToCompletion();
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setOtpError("");
    const res = await resendOtp(form.email);
    if (res.ok) {
      toast.success("A fresh code was sent to your email.");
      setResendCooldown(60);
    } else {
      setOtpError(res.error);
    }
  };

  const handleSubscribeAndPay = () => {
    if ((planParam && planParam !== "Free") || packParam) {
      const targetBase = form.role === "recruiter" ? "/recruiter/pricing" : "/app/pricing";
      navigate(`${targetBase}${location.search}`);
    } else {
      navigate(form.role === "recruiter" ? "/recruiter" : "/app");
    }
  };

  const proceedToCompletion = async () => {
    setStage("processing");
    await new Promise((r) => setTimeout(r, seeker ? 2400 : 900));
    if (seeker) setStage("done");
    else { toast.success("Recruiter workspace ready"); navigate("/recruiter"); }
  };

  const canContinue = () => {
    if (step === 0) return form.name && form.email && form.password.length >= 6;
    return true;
  };

  const submitStep = () => {
    if (step < steps.length - 1) next();
    else finish();
  };

  if (stage === "processing") return <Processing seeker={seeker} />;
  if (stage === "done") {
    return (
      <Success
        onEnter={() => navigate("/app")}
        name={form.name}
        plan={planParam}
        pack={packParam}
        onSubscribeAndPay={handleSubscribeAndPay}
        loadingCheckout={loadingCheckout}
      />
    );
  }
  if (stage === "otp") {
    return (
      <OtpVerificationScreen
        email={form.email}
        otp={otp}
        setOtp={setOtp}
        onVerify={handleVerifyOtp}
        onResend={handleResend}
        onBack={() => setStage("form")}
        verifying={verifying}
        error={otpError}
        cooldown={resendCooldown}
      />
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10">
      <WorldMapBackground />
      <div className="absolute left-5 top-5"><Logo /></div>
      <div className="absolute right-5 top-5"><ThemeToggle /></div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="card-surface p-8 shadow-float">
          {/* progress */}
          <div className="mb-6 flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex flex-1 items-center gap-2">
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-electric" : "bg-secondary"}`} />
              </div>
            ))}
          </div>
          <p className="label-caps mb-1">Step {step + 1} of {steps.length}</p>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
              {step === 0 && <AccountStep form={form} set={set} plan={planParam} pack={packParam} />}
              {seeker && step === 1 && <RoleStep form={form} set={set} />}
              {seeker && step === 2 && <PrefStep form={form} set={set} />}
              {seeker && step === 3 && <CvStep form={form} set={set} />}
              {!seeker && step === 1 && <CompanyStep form={form} set={set} />}
            </motion.div>
          </AnimatePresence>

          {error && <p className="mt-4 text-sm font-medium text-destructive">{error}</p>}

          <div className="mt-8 flex items-center gap-3">
            {step > 0 && (
              <button onClick={back} className="cv-btn-ghost border border-border" data-testid="signup-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            <button onClick={submitStep} disabled={!canContinue()} className="cv-btn-primary flex-1" data-testid="signup-next">
              {step < steps.length - 1 ? <>Continue <ArrowRight className="h-4 w-4" /></> : <>Build my Career Profile <Sparkles className="h-4 w-4" /></>}
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to={`/login${location.search}`} className="font-semibold text-electric">Log in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="label-caps">{label}</label>
      <input
        {...props}
        className="mt-1.5 w-full rounded-xl border border-border bg-card/60 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-all"
      />
    </div>
  );
}

function AccountStep({ form, set, plan, pack }) {
  return (
    <div className="space-y-4">
      {(plan || pack) && (
        <div className="inline-flex items-center gap-1.5 rounded-full border border-electric/30 bg-electric/10 px-3 py-1 text-xs font-semibold text-electric">
          <Sparkles className="h-3.5 w-3.5" /> Subscribing to: {plan || pack}
        </div>
      )}
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Create your account</h1>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => set("role", "job_seeker")} data-testid="role-seeker"
          className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-colors ${form.role === "job_seeker" ? "border-electric bg-electric/5" : "border-border"}`}>
          <Briefcase className="h-5 w-5 text-electric" />
          <span className="font-heading font-bold">Job Seeker</span>
          <span className="text-xs text-muted-foreground">Find, apply, prepare</span>
        </button>
        <button onClick={() => set("role", "recruiter")} data-testid="role-recruiter"
          className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-colors ${form.role === "recruiter" ? "border-violet bg-violet/5" : "border-border"}`}>
          <Building2 className="h-5 w-5 text-violet" />
          <span className="font-heading font-bold">Recruiter</span>
          <span className="text-xs text-muted-foreground">Source & hire talent</span>
        </button>
      </div>
      <Field label="Full name" data-testid="signup-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Alex Morgan" />
      <Field label="Email" data-testid="signup-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
      <Field label="Password" data-testid="signup-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" />
    </div>
  );
}

function RoleStep({ form, set }) {
  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Tell us about your work</h1>
      <Field label="Current role" value={form.current_role} onChange={(e) => set("current_role", e.target.value)} placeholder="Product Analyst" />
      <Field label="Years of experience" type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} placeholder="4.8" />
      <Field label="Target role" value={form.target_role} onChange={(e) => set("target_role", e.target.value)} placeholder="Product Manager" />
    </div>
  );
}

function PrefStep({ form, set }) {
  const toggleModel = (m) => {
    const list = form.work_models || [];
    if (list.includes(m)) {
      if (list.length === 1) return;
      set("work_models", list.filter((x) => x !== m));
    } else {
      set("work_models", [...list, m]);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Where do you want to work?</h1>
      <Field label="Preferred locations" value={form.locations} onChange={(e) => set("locations", e.target.value)} placeholder="London, Remote, Berlin" />
      <div>
        <div className="flex items-center justify-between">
          <label className="label-caps">Work model</label>
          <span className="text-xs text-muted-foreground">Select all that apply</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {WORK_MODELS.map((m) => {
            const isSelected = (form.work_models || []).includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleModel(m)}
                className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                  isSelected
                    ? "border-electric bg-electric text-white shadow-xs"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {isSelected && <Check className="h-4 w-4 stroke-[2.5]" />}
                {m}
              </button>
            );
          })}
        </div>
      </div>
      <div className="min-w-0">
        <label className="label-caps">Target salary</label>
        <div className="mt-2 flex gap-2 min-w-0">
          <select
            value={form.salary_currency || "USD"}
            onChange={(e) => set("salary_currency", e.target.value)}
            className="shrink-0 rounded-2xl border border-border bg-card px-3 py-3 text-sm font-semibold focus:border-electric focus:outline-none"
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
          <input
            type="text"
            value={form.target_salary}
            onChange={(e) => set("target_salary", e.target.value)}
            placeholder="140k"
            className="min-w-0 flex-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm font-medium focus:border-electric focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}

function CvStep({ form, set }) {
  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">Upload your CV</h1>
      <p className="text-sm text-muted-foreground">We'll parse it into your Career Profile. Never fabricated.</p>
      <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-secondary/40 p-10 text-center transition-colors hover:border-electric">
        <UploadCloud className="h-8 w-8 text-electric" />
        <span className="font-semibold">{form.cv_name || "Drop your CV or click to browse"}</span>
        <span className="text-xs text-muted-foreground">PDF, DOCX up to 5MB</span>
        <input type="file" className="hidden" onChange={(e) => set("cv_name", e.target.files?.[0]?.name || "resume.pdf")} />
      </label>
      <button onClick={() => set("cv_name", "Alex_Morgan_CV.pdf")} className="text-xs font-semibold text-electric">Use demo CV instead</button>
    </div>
  );
}

function CompanyStep({ form, set }) {
  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">About your company</h1>
      <Field label="Company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Nimbus Labs" />
      <Field label="Your title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Head of Talent" />
    </div>
  );
}

function Processing({ seeker }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5">
      <WorldMapBackground />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
        <div className="mx-auto mb-8 h-20 w-20 animate-spin rounded-full border-4 border-electric border-t-transparent" />
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">
          {seeker ? "Building your Career Profile…" : "Setting up your workspace…"}
        </h1>
        <div className="mx-auto mt-6 max-w-sm space-y-2 text-left">
          {["Parsing your CV", "Extracting skills & experience", "Matching global opportunities", "Scoring your profile"].map((t, i) => (
            <motion.div key={t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.5 }}
              className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-lime" /> {t}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function Success({ onEnter, name, plan, pack, onSubscribeAndPay, loadingCheckout }) {
  const isPaidChoice = (plan && plan !== "Free") || Boolean(pack);
  const planDetails = {
    "Career Pro": { price: "$24.99/mo", badge: "⚡ 100 Monthly AI Credits", desc: "Unlimited Job Searches, Deep ATS intelligence & CV Rewriting." },
    "Interview Pro": { price: "$29.99/mo", badge: "⚡ 150 Monthly AI Credits", desc: "Live Face-to-Face voice mock rooms & executive suite." },
    "Quick Pack": { price: "$9.99", badge: "⚡ 30 Top-Up Credits", desc: "Ideal for upcoming interview rounds & ATS optimizations." },
    "Sprint Pack": { price: "$17.99", badge: "⚡ 60 Top-Up Credits", desc: "20 CV custom revisions & full STAR interview feedback." },
    "Intensive Pack": { price: "$27.99", badge: "⚡ 100 Top-Up Credits", desc: "Comprehensive career sprint with highest savings per credit." },
  };
  const selectedInfo = planDetails[plan || pack] || { price: "$24.99/mo", badge: "⚡ Premium AI Suite", desc: "Unlock unlimited intelligence, ATS scans, and AI CV tools." };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10">
      <WorldMapBackground />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="card-surface p-8 text-center shadow-float">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-lime/15">
            <Check className="h-7 w-7 text-lime" />
          </div>
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-lime/30 bg-lime/10 px-3.5 py-1 text-xs font-bold text-lime">
            <Zap className="h-3.5 w-3.5 fill-lime" /> +10 Free AI Credits Added to Wallet
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Career Profile created</h1>
          <p className="mt-2 text-sm text-muted-foreground">Welcome to CVOLVE, {name?.split(" ")[0]}.</p>
          
          <div className="my-5 flex items-center justify-center gap-6">
            <div className="text-center">
              <MatchRing value={90} size={84} stroke={8} color="#06B6D4" />
              <p className="mt-2 text-xs font-semibold text-muted-foreground">Profile strength</p>
            </div>
          </div>

          {isPaidChoice ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-electric/30 bg-electric/5 p-4 text-left">
                <div className="flex items-center justify-between">
                  <span className="label-caps font-bold text-electric">{plan || pack}</span>
                  <span className="font-heading text-sm font-bold text-foreground">{selectedInfo.price}</span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-electric">
                  <span>{selectedInfo.badge}</span>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{selectedInfo.desc}</p>
              </div>

              <button
                onClick={onSubscribeAndPay}
                data-testid="enter-app-subscribe"
                className="cv-btn-primary w-full py-3 flex items-center justify-center gap-2 shadow-glow text-sm font-semibold"
              >
                Proceed to {plan || pack} & Subscribe <ArrowRight className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={onEnter}
                data-testid="enter-app-skip"
                className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip for now and explore free features →
              </button>
            </div>
          ) : (
            <button onClick={onEnter} data-testid="enter-app" className="cv-btn-primary w-full mt-2">
              Explore your job matches <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function OtpVerificationScreen({ email, otp, setOtp, onVerify, onResend, onBack, verifying, error, cooldown }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5 py-10">
      <WorldMapBackground />
      <div className="absolute left-5 top-5"><Logo /></div>
      <div className="absolute right-5 top-5"><ThemeToggle /></div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md">
        <div className="card-surface p-8 shadow-float">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-electric/10 text-electric">
            <Mail className="h-8 w-8" />
          </div>

          <div className="text-center">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Check your email</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We sent a 6-digit verification code to
            </p>
            <div className="mt-1.5 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-semibold text-foreground">
              <span>{email}</span>
              <button onClick={onBack} title="Edit email" className="text-muted-foreground hover:text-electric transition-colors">
                <Edit2 className="h-3 w-3" />
              </button>
            </div>
          </div>

          <form onSubmit={onVerify} className="mt-8 space-y-5">
            <div>
              <label className="label-caps block text-center mb-2">Verification code</label>
              <input
                data-testid="otp-input"
                type="text"
                maxLength={6}
                autoFocus
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9A-Za-z]/g, "").toUpperCase())}
                placeholder="123456"
                className="w-full text-center text-3xl font-mono font-bold tracking-[10px] rounded-xl border border-border bg-card/60 py-3.5 text-foreground placeholder:text-muted-foreground outline-none transition-all focus:border-electric focus:ring-2 focus:ring-electric/20 uppercase"
              />
            </div>

            {error && (
              <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} data-testid="otp-error" className="text-center text-xs font-semibold text-destructive">
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={verifying || otp.length < 6}
              data-testid="otp-submit"
              className="cv-btn-primary w-full py-3 text-sm font-semibold"
            >
              {verifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  Verify & Activate Account <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 flex flex-col items-center gap-3 border-t border-border/60 pt-5 text-center text-xs">
            <p className="text-muted-foreground">
              Didn't receive the email?
            </p>
            <button
              onClick={onResend}
              disabled={cooldown > 0}
              data-testid="otp-resend"
              className={`font-semibold transition-colors ${cooldown > 0 ? "text-muted-foreground cursor-not-allowed" : "text-electric hover:underline"}`}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Click to resend code"}
            </button>

            <button
              onClick={onBack}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to registration
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
